import React, { useState, useEffect, useRef } from 'react';
import FormCodeGenerator from './FormCodeGenerator';
import LawyerInfoTab from './LawyerInfoTab';
import { apiClient, API_BASE_URL } from '../services/api';
import ConsultationSummaries from './ConsultationSummaries';
import './InitialConsultation.css';

interface ConsultationForm {
  id: string;
  form_id: string;
  form_title: string;
  form_description: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  country: string;
  is_active: boolean;
  expires_at: string;
  created_at: string;
  form_link: string;
}

interface Submission {
  id: string;
  client_name: string;
  client_email: string;
  submitted_at: string;
  status: string;
  form_data: any;
}

interface InitialConsultationProps {
  initialTab?: 'forms' | 'summaries';
}

const InitialConsultation: React.FC<InitialConsultationProps> = ({ initialTab = 'forms' }) => {
  const [forms, setForms] = useState<ConsultationForm[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'active' | 'expired'>('active');
  const [activeTab, setActiveTab] = useState<'forms' | 'summaries'>('forms');
  
  // Add state for consultation summaries data
  const [clients, setClients] = useState<any[]>([]);
  const [summaries, setSummaries] = useState<any[]>([]);
  const [summariesLoading, setSummariesLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFormLinkModal, setShowFormLinkModal] = useState(false);
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [selectedForm, setSelectedForm] = useState<ConsultationForm | null>(null);
  const [formLink, setFormLink] = useState('');
  const [showTile, setShowTile] = useState(false); 
  const [latestCount, setLatestCount] = useState(10); // default show 5 latest forms
  const [emailProvided, setEmailProvided] = useState(false);
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    country: 'Canada',
    visa_type: 'express_entry',
    // US-specific fields
    full_name: '',
    email: '',
    phone: '',
    company: '',
    linkedin_profile: '',
    country_of_birth: '',
    nationality: '',
    current_visa_status: '',
    visa_sponsor: '',
    resume_text: '',
    additional_info: ''
  });
  const [usageData, setUsageData] = useState<{
    current: number;
    limit: number | null;
    remaining: number | null;
  } | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [limitReachedMessage, setLimitReachedMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  // state declarations
  const [publicIntakeLink, setPublicIntakeLink] = useState<{ linkId: string; publicUrl: string } | null>(null);
  const [loadingPublicLink, setLoadingPublicLink] = useState<boolean>(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [loadingQRCode, setLoadingQRCode] = useState<boolean>(false);
  const [showQRCode, setShowQRCode] = useState<boolean>(false);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    fetchForms();
    fetchUsageData();
    fetchConsultationSummaries();
    fetchFirmLink();
  }, []);

  // Always refresh summaries when switching to the 'summaries' tab
  useEffect(() => {
    if (activeTab === 'summaries') {
      fetchConsultationSummaries();
    } else if (activeTab === 'forms') {
      // Refresh usage data when switching to forms tab to ensure count is current
      fetchUsageData();
    }
  }, [activeTab]);

  const fetchConsultationSummaries = async () => {
    try {
      setSummariesLoading(true);
      console.log('🔄 Fetching consultation summaries data...');
      
      // Fetch all consultation form submissions (clients)
      const clientsResponse = await apiClient.get('/consultation-forms/submissions');
      setClients(clientsResponse || []);
      
      // Fetch existing summaries
      const summariesResponse = await apiClient.get('/consultation-forms/summaries');
      setSummaries(summariesResponse || []);
      
      console.log('✅ Consultation summaries data fetched successfully');
      
    } catch (error) {
      console.error('❌ Error fetching consultation summaries data:', error);
    } finally {
      setSummariesLoading(false);
    }
  };

  // Auto-close form link modal after 5 seconds
  useEffect(() => {
    if (showFormLinkModal) {
      const timer = setTimeout(() => {
        setShowFormLinkModal(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showFormLinkModal]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/consultation-forms');
      setForms(response);
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageData = async () => {
    try {
      const response = await apiClient.get('/subscriptions/usage');
      if (response.usage) {
        setUsageData({
          current: response.usage.summaries_generated || 0,
          limit: response.usage.summaries_limit,
          remaining: response.usage.summaries_remaining || 0
        });
      }
    } catch (error: any) {
      console.error('Error fetching usage data:', error);
      // If user doesn't have a subscription, just set usage to null
      // The App.tsx will handle showing the subscription selection page
      if (error.response?.status === 404 && error.response?.data?.needsSubscription) {
        console.log('User needs subscription - usage data unavailable');
        setUsageData(null);
      }
    }
  };

  const fetchSubmissions = async (formId: string) => {
    try {
      const response = await apiClient.get(`/consultation-forms/${formId}/submissions`);
      setSubmissions(response);
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleCreateConsultation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const hasEmail = formData.country === 'United States' 
        ? (formData.email && formData.email.trim())
        : (formData.client_email && formData.client_email.trim());
      
      // Prepare data based on country
      let submissionData = { ...formData };
      if (formData.country === 'United States') {
        // For US forms, use full_name as client_name and email as client_email
        submissionData.client_name = formData.full_name;
        submissionData.client_email = formData.email;
      }
      
      console.log('🔍 Frontend form data being sent:', {
        originalFormData: formData,
        submissionData,
        country: formData.country,
        hasEmail: formData.country === 'United States' ? formData.email : formData.client_email
      });
      
      const response = await apiClient.post('/consultation-forms', submissionData);
      setShowCreateModal(false);
      setFormLink(response.formLink);
      setEmailProvided(!!hasEmail);
      setShowFormLinkModal(true);
      setFormData({ 
        client_name: '', 
        client_email: '', 
        client_phone: '', 
        country: 'Canada', 
        visa_type: 'express_entry',
        full_name: '',
        email: '',
        phone: '',
        company: '',
        linkedin_profile: '',
        country_of_birth: '',
        nationality: '',
        current_visa_status: '',
        visa_sponsor: '',
        resume_text: '',
        additional_info: ''
      });
      fetchForms(); // Refresh the forms list
      fetchUsageData(); // Refresh usage data
    } catch (error: any) {
      console.error('Error creating form:', error);
      if (error.response?.status === 403 && error.response?.data?.needsUpgrade) {
        setLimitReachedMessage(error.response.data.message || 'You have reached your consultation form limit');
        setShowCreateModal(false);
        setShowUpgradeModal(true);
      } else {
        alert(error.response?.data?.error || 'Failed to create form');
      }
    }
  };

  const isLimitReached = () => {
    if (!usageData) return false;
    if (usageData.limit === null) return false; // Unlimited
    return usageData.current >= usageData.limit;
  };

  const handleExpireForm = async (formId: string) => {
    try {
      console.log('🔄 Attempting to expire form:', formId);
      
      const response = await apiClient.post(`/consultation-forms/${formId}/expire`);
      console.log('✅ Form expired successfully:', response);

      // Update forms list locally
      setForms(prevForms =>
        prevForms.map(f =>
          f.id === formId ? { ...f, is_active: false } : f
        )
      );

      // Update selectedForm if it's the one that was expired
      if (selectedForm && selectedForm.id === formId) {
        setSelectedForm({ ...selectedForm, is_active: false });
        console.log('✅ Updated selectedForm to inactive');
      }

      // Close the modal after successful expiration
      setShowTile(false);

    } catch (error) {
      console.error('❌ Error expiring form:', error);
      alert('Failed to expire form. Please try again.');
    }
  };

  const handleViewTile = (form: ConsultationForm) => {
    setSelectedForm(form);
    setShowTile(true); // show the tile modal
  };

  const handleGenerateSummary = async (submissionId: string) => {
    try {
      await apiClient.post(`/consultation-forms/submissions/${submissionId}/generate-summary`);
      if (selectedForm) {
        fetchSubmissions(selectedForm.form_id);
      }
      // Refresh usage data after generating summary
      fetchUsageData();
    } catch (error) {
      console.error('Error generating summary:', error);
      const apiError = error as any;
      const status = apiError?.response?.status;
      const data = apiError?.response?.data || {};
      const serverMessage = data?.message || apiError?.message || 'Failed to generate summary. Please try again.';
      const usageDetails = typeof data?.current === 'number' && typeof data?.limit === 'number'
        ? ` (${data.current}/${data.limit} summaries used)`
        : '';

      if (status === 403) {
        alert(`${serverMessage}${usageDetails}`);
      } else {
        alert(serverMessage);
      }
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Form link copied to clipboard!');
  };

  const copyFormLink = (formLink: string) => {
    navigator.clipboard.writeText(formLink);
    alert('Form link copied to clipboard!');
  };

  const getStatusColor = (form: ConsultationForm) => {
    if (!form.is_active || new Date(form.expires_at) <= new Date()) {
      return '#ef4444';
    }
    return '#10b981';
  };

  const getStatusText = (form: ConsultationForm) => {
    if (!form.is_active || new Date(form.expires_at) <= new Date()) {
      return 'Expired';
    }
    return 'Active';
  };

  const filteredForms = forms.filter(form => {
    const matchesFilter =
      filter === 'active'
        ? form.is_active && new Date(form.expires_at) > new Date()
        : !form.is_active || new Date(form.expires_at) <= new Date();

    // Add null checks to prevent toLowerCase() error
    const clientName = form.client_name || '';
    const clientEmail = form.client_email || '';
    const query = searchQuery.toLowerCase();

    const matchesSearch =
      clientName.toLowerCase().includes(query) ||
      clientEmail.toLowerCase().includes(query);

    return matchesFilter && matchesSearch;
  });


  const handleViewSubmissions = (form: ConsultationForm) => {
    setSelectedForm(form);
    setShowSubmissions(true);
    fetchSubmissions(form.form_id);
  };

  const fetchFirmLink = async () => {
    try {
      setLoadingPublicLink(true);
      const response = await apiClient.getFirmLink();
      setPublicIntakeLink(response);
    } catch (error) {
      console.error('❌ Error fetching firm link:', error);
      setPublicIntakeLink(null);
    } finally {
      setLoadingPublicLink(false);
    }
  };

  const fetchQRCode = async () => {
    try {
      setLoadingQRCode(true);
      const response = await apiClient.getFirmLinkQRCode();
      setQrCode(response.qrCode);
      setShowQRCode(true);
    } catch (error) {
      console.error('❌ Error fetching QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    } finally {
      setLoadingQRCode(false);
    }
  };

  const downloadQRCode = () => {
    if (!qrCode) return;

    const link = document.createElement('a');
    link.href = qrCode;
    link.download = 'consultation-form-qr-code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyLink = async () => {
    if (!publicIntakeLink?.publicUrl) return;

    try {
      await navigator.clipboard.writeText(publicIntakeLink.publicUrl);
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (error) {
      console.error('❌ Failed to copy link:', error);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const renderFirmLinkSection = () => (
    <section className="initial-consultation__firm-link">
      <div className="firm-link-card">
        <div className="firm-link-header">
          <h3>Your Public Intake Link</h3>
          <p>Share this link with clients via email, social media, or QR code.</p>
        </div>

        {loadingPublicLink ? (
          <div className="firm-link-loading">
            <div className="spinner" />
            <span>Generating link…</span>
          </div>
        ) : publicIntakeLink ? (
          <div className="firm-link-body">
            <div className="firm-link-url">
              <input type="text" value={publicIntakeLink.publicUrl} readOnly />
              <button onClick={handleCopyLink} className={copyStatus === 'copied' ? 'copied' : ''}>
                {copyStatus === 'copied' ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
            
            <div className="firm-link-actions">
              <a
                href={`/intake/${publicIntakeLink.linkId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="firm-link-preview"
              >
                Preview intake page
              </a>
              
              <button 
                onClick={fetchQRCode} 
                className="qr-code-btn"
                disabled={loadingQRCode}
              >
                {loadingQRCode ? 'Generating...' : showQRCode ? 'Regenerate QR Code' : 'Generate QR Code'}
              </button>
            </div>

            {showQRCode && qrCode && (
              <div className="qr-code-section">
                <div className="qr-code-header">
                  <h4>QR Code</h4>
                  <p>Scan to access the consultation form</p>
                </div>
                <div className="qr-code-container">
                  <img src={qrCode} alt="QR Code for consultation form" />
                </div>
                <button onClick={downloadQRCode} className="download-qr-btn">
                  Download QR Code
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="firm-link-error">
            <p>We couldn’t retrieve your public link. Please try again later.</p>
          </div>
        )}
      </div>
    </section>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading consultation forms...</p>
      </div>
    );
  }

  return (
    <div className="initial-consultation">
      {/* Header Section */}
      <div className="consultation-header">
        <h1>
          {activeTab === 'forms' ? 'Consultation Forms' : 'Consultation Summaries'}
        </h1>
        {activeTab === 'forms' && (
          <p>Create and manage consultation forms for your clients</p>
        )}
        {usageData && activeTab === 'forms' && (
          <div className="usage-info">
            <span className={`usage-text ${isLimitReached() ? 'limit-reached' : ''}`}>
              {usageData.current} / {usageData.limit === null ? '∞' : usageData.limit} summaries used
            </span>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab-button ${activeTab === 'forms' ? 'active' : ''}`}
          onClick={() => setActiveTab('forms')}
        >
          Consultation Forms
        </button>
        <button 
          className={`tab-button ${activeTab === 'summaries' ? 'active' : ''}`}
          onClick={() => setActiveTab('summaries')}
        >
          Consultation Summaries
        </button>
      </div>

      {/* Forms Tab */}
      {activeTab === 'forms' && (
        <>
          {renderFirmLinkSection()}
          <div className="forms-controls">
            <div className="controls-left">
              <div className="filter-section">
                <label>Show:</label>
                <select 
                  value={filter} 
                  onChange={(e) => setFilter(e.target.value as 'active' | 'expired')}
                >
                  <option value="active">
                    Active ({forms.filter(f => f.is_active && new Date(f.expires_at) > new Date()).length})
                  </option>
                  <option value="expired">
                    Expired ({forms.filter(f => !f.is_active || new Date(f.expires_at) <= new Date()).length})
                  </option>
                </select>
              </div>
              
              <div className="search-section">
                <input
                  type="text"
                  placeholder="Search by client name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="controls-right">
              <div className="slider-section">
                <label>Show latest {latestCount} forms:</label>
                <input
                  type="range"
                  min={1}
                  max={filteredForms.length || 1}
                  value={latestCount}
                  onChange={(e) => setLatestCount(Number(e.target.value))}
                />
              </div>
              
              <button 
                className={`create-form-btn ${isLimitReached() ? 'disabled' : ''}`}
                onClick={() => !isLimitReached() && setShowCreateModal(true)}
                disabled={isLimitReached()}
                title={isLimitReached() ? 'Usage limit reached. Please upgrade your plan.' : 'Create a new consultation form'}
              >
                + Create New Form
              </button>
            </div>
          </div>

          {filteredForms.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📄</div>
              <h3>No consultation forms found</h3>
              <p>Create your first consultation form to get started</p>
              <button 
                className="create-btn"
                onClick={() => setShowCreateModal(true)}
              >
                Create Form
              </button>
            </div>
          ) : (
            <ul className="forms-list">
              {filteredForms
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .slice(0, latestCount)
                .map(form => (
                <li key={form.id} className="form-list-item">
                  <span>{form.form_title}</span>
                  <button
                    className="view-details-btn"
                    onClick={() => handleViewTile(form)}
                  >
                    View Details
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* Summaries Tab */}
      <div style={{ display: activeTab === 'summaries' ? 'block' : 'none' }}>
        <ConsultationSummaries 
          onSummaryGenerated={fetchUsageData}
          clients={clients}
          summaries={summaries}
          loading={summariesLoading}
          onRefreshSummaries={fetchConsultationSummaries}
        />
      </div>

      {/* Create Form Modal */}
      {showCreateModal && (
        <div className="modal-overlay consultation-modal-overlay">
          <div className="modal modal-large consultation-modal">
            <div className="modal-header">
              <h2>Create Initial Consultation Form</h2>
              <button
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
                title="Close (Esc)"
                aria-label="Close modal"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-creation-content">
                <div className="creation-step">
                  <h3>Step 1: Form Configuration</h3>

                  <form onSubmit={handleCreateConsultation}>
                    {/* Client Name / Full Name - conditional based on country */}
                    {formData.country === 'Canada' ? (
                      <div className="form-group">
                        <label>Client Name *</label>
                        <input
                          type="text"
                          value={formData.client_name}
                          onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                          placeholder="Enter client name"
                          required
                        />
                        <small className="form-help">
                          Enter the client's name for this intake form.
                        </small>
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          value={formData.full_name}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          placeholder="Enter full name"
                          required
                        />
                        <small className="form-help">
                          Enter the client's full name for this intake form.
                        </small>
                      </div>
                    )}

                    {/* Email field - conditional based on country */}
                    {formData.country === 'Canada' ? (
                      <div className="form-group">
                        <label>Client Email (to send link)</label>
                        <input
                          type="email"
                          value={formData.client_email}
                          onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                          placeholder="client.email@example.com"
                        />
                        <small className="form-help">
                          Optional. If provided, an email with the form link will be sent to the client.
                        </small>
                      </div>
                    ) : (
                      <div className="form-group">
                        <label>Email (to send link)</label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="client.email@example.com"
                        />
                        <small className="form-help">
                          Optional. If provided, an email with the form link will be sent to the client.
                        </small>
                      </div>
                    )}

                    <div className="form-group">
                      <label>Client Phone (optional)</label>
                      <input
                        type="tel"
                        value={formData.client_phone}
                        onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                        placeholder="Enter client phone number (optional)"
                      />
                    </div>

                    <div className="form-group">
                      <label>Country *</label>
                      <select
                        value={formData.country}
                        onChange={(e) => {
                          const newCountry = e.target.value;
                          const currentCountry = formData.country;
                          
                          // Preserve name and email fields when switching countries
                          let updatedFormData = { ...formData };
                          
                          // Always sync name fields in both directions to preserve user input
                          // If switching from Canada to US: copy client_name to full_name
                          if (currentCountry === 'Canada' && newCountry === 'United States') {
                            if (formData.client_name) {
                              updatedFormData.full_name = formData.client_name;
                            }
                            if (formData.client_email) {
                              updatedFormData.email = formData.client_email;
                            }
                          }
                          
                          // If switching from US to Canada: copy full_name to client_name
                          // Also preserve full_name itself so it's available when switching back
                          if (currentCountry === 'United States' && newCountry === 'Canada') {
                            if (formData.full_name) {
                              updatedFormData.client_name = formData.full_name;
                              // Explicitly preserve full_name in the state
                              updatedFormData.full_name = formData.full_name;
                            }
                            if (formData.email) {
                              updatedFormData.client_email = formData.email;
                              // Explicitly preserve email in the state
                              updatedFormData.email = formData.email;
                            }
                          }
                          
                          setFormData({
                            ...updatedFormData,
                            country: newCountry,
                            // Reset visa_type when country changes
                            visa_type: newCountry === 'Canada' ? 'express_entry' : newCountry === 'United States' ? 'EB1' : 'express_entry'
                          });
                        }}
                      >
                        <option value="Canada">Canada</option>
                        <option value="United States">United States</option>
                      </select>
                      <small className="form-help">
                        Select the country for which the intake form is intended.
                      </small>
                    </div>

                    {/* Visa Type dropdown - show for both Canada and US */}
                    {formData.country && (
                      <div className="form-group">
                        <label>Visa Type *</label>
                        <select
                          value={formData.visa_type}
                          onChange={(e) => setFormData({...formData, visa_type: e.target.value})}
                        >
                          {formData.country === 'Canada' ? (
                            <>
                              <option value="express_entry">Express Entry</option>
                              <option value="business_immigration">Business Immigration</option>
                              <option value="family_sponsorship">Family Sponsorship (Spouse)</option>
                              <option value="study_permit">Study Permit Pathway</option>
                            </>
                          ) : formData.country === 'United States' ? (
                            <>
                              <option value="EB1">EB1 - Extraordinary Ability</option>
                              <option value="O1">O1 - Extraordinary Ability</option>
                              <option value="H1B">H-1B - Specialty Occupation</option>
                              <option value="J1">J-1 - Exchange Visitor</option>
                              <option value="L1">L-1 - Intracompany Transferee</option>
                            </>
                          ) : null}
                        </select>
                        <small className="form-help">
                          {formData.country === 'Canada' 
                            ? 'Select the type of Canadian immigration program for this consultation.'
                            : 'Select the type of US immigration program for this consultation.'
                          }
                        </small>
                      </div>
                    )}

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setShowCreateModal(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-primary"
                      >
                        Create Form
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTile && selectedForm && (
      <div className="modal-overlay" onClick={() => setShowTile(false)}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn-alt" onClick={() => setShowTile(false)} title="Close">✕</button>
          <div className="form-card">
            <div className="form-header">
              <h3>{selectedForm.form_title}</h3>
              <div className="form-status" style={{ color: getStatusColor(selectedForm) }}>
                {getStatusText(selectedForm)}
              </div>
            </div>
            <div className="form-details">
              <p><strong>Client:</strong> {selectedForm.client_name}</p>
              <p><strong>Email:</strong> {selectedForm.client_email}</p>
              <p><strong>Country:</strong> {selectedForm.country}</p>
              <p><strong>Expires:</strong> {new Date(selectedForm.expires_at).toLocaleDateString()}</p>
            </div>
            <div className="form-actions">
              {selectedForm.is_active && new Date(selectedForm.expires_at) > new Date() && (
                <button 
                  className="copy-link-btn"
                  onClick={() => copyFormLink(selectedForm.form_link)}
                  title="Copy form link to share with client"
                >
                  📋 Copy Link
                </button>
              )}

              <button 
                className="view-submissions-btn"
                onClick={() => {
                  setShowSubmissions(true);
                  setShowTile(false); // optionally close tile modal when opening submissions
                  fetchSubmissions(selectedForm.form_id);
                }}
              >
                View Submissions
              </button>

              {selectedForm.is_active && new Date(selectedForm.expires_at) > new Date() && (
                <button 
                  className="expire-btn"
                  onClick={() => handleExpireForm(selectedForm.id)}
                >
                  Expire Form
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )}


      {/* Form Link Modal */}
      {showFormLinkModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Form Created Successfully!</h2>
              <button 
                className="close-btn-alt"
                onClick={() => setShowFormLinkModal(false)}
                title="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="success-message">
                <div className="success-icon">✅</div>
                <h3>Form Created & Code Generated!</h3>
                <p>
                  {emailProvided 
                    ? 'Your consultation form has been created and an email has been sent to the client.'
                    : 'Your consultation form has been created and is ready to share with your client.'
                  }
                </p>
              </div>
              
              <div className="form-url-section">
                <label>Form URL</label>
                <div className="url-container">
                  <input 
                    type="text" 
                    value={formLink} 
                    readOnly 
                    className="url-input"
                  />
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(formLink)}
                  >
                    📋 Copy
                  </button>
                </div>
              </div>

              <div className="next-steps">
                <h4>Next Steps:</h4>
                <ul>
                  {!emailProvided && <li>Copy and share the link with your client</li>}
                  {emailProvided && <li>Client will receive the form link via email</li>}
                  <li>Client can access the form using the shareable link</li>
                  <li>Monitor submissions in the dashboard</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {showSubmissions && selectedForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Submissions for {selectedForm.form_title}</h2>
              <button 
                className="close-btn-alt"
                onClick={() => setShowSubmissions(false)}
                title="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {submissions.length === 0 ? (
                <p>No submissions yet.</p>
              ) : (
                <div className="submissions-list">
                  {submissions.map(submission => (
                    <div key={submission.id} className="submission-item">
                      <div className="submission-info">
                        <h4>{submission.client_name}</h4>
                        <p>{submission.client_email}</p>
                        <p>Submitted: {new Date(submission.submitted_at).toLocaleString()}</p>
                        <p>Status: {submission.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay">
          <div className="modal upgrade-modal">
            <div className="modal-header">
              <h2>⚠️ Usage Limit Reached</h2>
              <button 
                className="close-btn-alt"
                onClick={() => setShowUpgradeModal(false)}
                title="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="upgrade-content">
                <p className="upgrade-message">{limitReachedMessage}</p>
                {usageData && (
                  <div className="usage-stats">
                    <div className="stat-item">
                      <span className="stat-label">Current Usage:</span>
                      <span className="stat-value">{usageData.current} / {usageData.limit}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Remaining:</span>
                      <span className="stat-value">0</span>
                    </div>
                  </div>
                )}
                <div className="upgrade-actions">
                  <button 
                    className="upgrade-btn"
                    onClick={() => {
                      setShowUpgradeModal(false);
                      window.location.href = '#settings';
                      // Trigger settings navigation
                      const settingsEvent = new CustomEvent('navigate-to-settings');
                      window.dispatchEvent(settingsEvent);
                    }}
                  >
                    Upgrade Plan
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={() => setShowUpgradeModal(false)}
                  >
                    Maybe Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InitialConsultation;
